import { useState } from "react"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import EditableTagCell from "./EditableTagCell"
import {
  getAssignedCrew,
  getAvailableStaff,
  assignMultipleStaff,
  unassignStaffMember,
  selectAssignedCrew,
  selectUnassignOperationLoading
} from "store/staffAssignments"
import styles from "./BookingTable.module.css"

const renderCrewLabel = (member) => {
  const roleSuffix = member.role ? ` (${member.role})` : ""
  return `${member.name}${roleSuffix}`
}

// filter=all returns every crew member across availability buckets; flatten them into one
// list and split by each member's is_assigned flag (see handlePickerOpen).
const normalizeStaffList = (data) => {
  if (Array.isArray(data)) return data
  if (data && typeof data === "object") return [...(data.available || []), ...(data.unavailable || []), ...(data.has_bookings || [])]
  return []
}

const toCrewItem = (staff) => {
  const primaryRole = staff.role || staff.primary_role || staff.roles?.[0]
  return {
    id: staff.id,
    name: staff.full_name || `${staff.first_name || ""} ${staff.last_name || ""}`.trim(),
    role: typeof primaryRole === "object" ? primaryRole?.name || "" : primaryRole || ""
  }
}

const CrewCell = ({ initialCrew, bookingId, emptyLabel, alwaysEditable }) => {
  const dispatch = useDispatch()
  const assignedCrew = useSelector(selectAssignedCrew)
  const removeLoading = useSelector(selectUnassignOperationLoading)
  const [groups, setGroups] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleEditStart = () => {
    if (bookingId) dispatch(getAssignedCrew(bookingId))
  }

  const handlePickerOpen = () => {
    if (!bookingId) return
    // A single filter=all call returns every crew member with an is_assigned flag; split it
    // into the two groups client-side (is_assigned → Available, else Other Crew).
    setIsLoading(true)
    dispatch(getAvailableStaff(bookingId, { filter: "all" }))
      .then((res) => {
        const staff = normalizeStaffList(res?.data)
        setGroups([
          { key: "available", label: "Available", items: staff.filter((member) => member.is_assigned).map(toCrewItem) },
          { key: "others", label: "Other Crew", items: staff.filter((member) => !member.is_assigned).map(toCrewItem) }
        ])
      })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }

  const handleAdd = (items) => {
    if (!bookingId) return
    dispatch(assignMultipleStaff(bookingId, items.map((item) => ({ profile_id: item.id, role: "crew_member" }))))
  }

  const handleRemove = (item) => {
    // Detail chips carry assignmentId directly; picker-added chips fall back to the
    // fetched assigned list, matching by profile id.
    const assignmentId =
      item.assignmentId ?? assignedCrew.find((a) => a.profileId === item.profileId || a.profileId === item.id)?.id
    if (assignmentId && bookingId) return dispatch(unassignStaffMember(assignmentId, bookingId))
    return undefined
  }

  return (
    <EditableTagCell
      initialItems={initialCrew}
      groups={groups}
      renderLabel={renderCrewLabel}
      addLabel="Add Crew"
      chipClassName={styles.crewLabel}
      onEditStart={handleEditStart}
      onPickerOpen={handlePickerOpen}
      onAdd={handleAdd}
      onRemove={handleRemove}
      loading={isLoading}
      removeLoading={removeLoading}
      emptyLabel={emptyLabel}
      alwaysEditable={alwaysEditable}
    />
  )
}

CrewCell.propTypes = {
  initialCrew: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string, role: PropTypes.string })),
  bookingId: PropTypes.string,
  emptyLabel: PropTypes.string,
  alwaysEditable: PropTypes.bool
}

CrewCell.defaultProps = {
  initialCrew: [],
  emptyLabel: "",
  alwaysEditable: false
}

export default CrewCell
